import api from './apiService';

/**
 * Service for order-related operations
 */
const orderService = {
  /**
   * Get current user's orders
   * @param {Object} options - Query options
   * @param {number} [options.limit=10] - Max number of results
   * @param {number} [options.offset=0] - Result offset
   * @returns {Promise<Object>} Orders with pagination
   */
  getUserOrders: async ({ limit = 10, offset = 0 } = {}) => {
    try {
      const response = await api.get('/orders/my', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch orders');
    }
  },

  /**
   * Get order by ID
   * @param {string} id - Order ID
   * @returns {Promise<Object>} Order details
   */
  getOrderById: async (id) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order');
    }
  },

  /**
   * Get order invoice
   * @param {string} id - Order ID
   * @returns {Promise<Object>} Invoice data
   */
  getOrderInvoice: async (id) => {
    try {
      const response = await api.get(`/orders/${id}/invoice`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice');
    }
  },

  /**
   * Get active subscription for current user
   * @returns {Promise<Object>} Active subscription details
   */
  getActiveSubscription: async () => {
    try {
      const response = await api.get('/orders/subscriptions/active');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch active subscription');
    }
  },

  /**
   * Get all subscriptions for current user
   * @param {Object} options - Query options
   * @param {number} [options.limit=10] - Max number of results
   * @param {number} [options.offset=0] - Result offset
   * @returns {Promise<Object>} Subscriptions with pagination
   */
  getUserSubscriptions: async ({ limit = 10, offset = 0 } = {}) => {
    try {
      const response = await api.get('/orders/subscriptions/my', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
    }
  },

  /**
   * Get subscription by ID
   * @param {string} id - Subscription ID
   * @returns {Promise<Object>} Subscription details
   */
  getSubscriptionById: async (id) => {
    try {
      const response = await api.get(`/orders/subscriptions/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
    }
  },

  /**
   * Cancel subscription at period end
   * @param {string} id - Subscription ID
   * @returns {Promise<Object>} Updated subscription
   */
  cancelSubscription: async (id) => {
    try {
      const response = await api.post(`/orders/subscriptions/${id}/cancel`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
    }
  },

  /**
   * Sync data from Polar.sh
   * @returns {Promise<Object>} Sync result
   */
  syncFromPolar: async () => {
    try {
      const response = await api.post('/orders/sync-from-polar');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to sync with Polar.sh');
    }
  },

  /**
   * Get Polar.sh customer state
   * @returns {Promise<Object>} Customer state
   */
  getPolarCustomerState: async () => {
    try {
      const response = await api.get('/orders/polar-customer-state');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch Polar.sh customer state');
    }
  },

  /**
   * Get user's current remaining credits
   * @returns {Promise<Object>} Credit information
   */
  getRemainingCredits: async () => {
    try {
      const response = await api.get('/credits/remaining');
      
      // If the API already separates credit types, use them
      const supportCredits = response.data.supportCredits !== undefined 
        ? response.data.supportCredits 
        : Math.floor(response.data.credits * 0.7); // Default split if not provided
      
      const developmentCredits = response.data.developmentCredits !== undefined
        ? response.data.developmentCredits
        : Math.floor(response.data.credits * 0.3); // Default split if not provided
      
      return {
        ...response.data,
        supportCredits,
        developmentCredits,
        credits: supportCredits + developmentCredits // Total credits is the sum of both types
      };
    } catch (error) {
      // If there's an error fetching credits, return 0 as default
      console.error('Error fetching remaining credits:', error);
      return { 
        success: true, 
        credits: 0,
        supportCredits: 0,
        developmentCredits: 0
      };
    }
  }
};

export default orderService;